import { TextField, Theme } from '@mui/material';
import {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createUseStyles } from 'react-jss';
import Icon from '@/components/icon';

interface Props {
  value: string;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  onEnter?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  disableHideInput?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  searchInput: {
    '&.MuiFormControl-root': {
      margin: '2px 0px',
    },
    '& label.MuiFormLabel-root.Mui-focused': {
      color: theme.palette.common.black,
    },
    '& .MuiInputBase-root': {
      paddingRight: 6,
      '& fieldset': {
        border: 'none !important',
      },
      '&.MuiInputBase-adornedStart': {
        paddingLeft: 0,
      },
      '& input.MuiInputBase-inputAdornedEnd': {
        paddingRight: 12,
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
    },
  },
  startAdornment: {
    marginTop: 2,
  },
  notchedOutline: {
    borderWidth: '1px !important',
  },
  root: {
    '& input.MuiInputBase-input': {
      fontSize: '0.9rem',
      padding: '0px 4px',
    },
    '& $startAdornment': {
      marginTop: 2,
    },
  },
}));

export interface SearchInputInterface {
  focus: () => void;
}

const SearchInput = forwardRef<SearchInputInterface, Props>(
  (
    {
      onChange,
      onEnter,
      value,
      disabled = false,
      placeholder = 'Search',
      disableHideInput = false,
    },
    ref
  ) => {
    const styles = useStyles();

    const [hideInput, setHideInput] = useState(!disableHideInput);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(String(event.target.value), event);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && onEnter) {
        event.preventDefault();
        onEnter(value);
      }
    };

    const handleBlur = () => {
      if (!value && !disableHideInput) {
        setHideInput(true);
      }
    };

    const handleClear = () => {
      onChange?.('');
    };

    let EndAdornmentEl: ReactNode = null;
    const showClearIcon = false && !!value;

    if (showClearIcon) {
      EndAdornmentEl = <Icon name="close" onClick={handleClear} />;
    }

    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => {
      return {
        focus: () => {
          inputRef.current?.focus();
        },
      };
    });

    const handleToggleInput = () => {
      const updated = !hideInput;
      setHideInput(updated);
      if (!updated) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 10);
      } else {
        onChange?.('');
      }
    };

    return (
      <TextField
        variant="outlined"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        value={value || ''}
        margin="none"
        fullWidth={false}
        disabled={hideInput}
        placeholder={hideInput ? '' : placeholder}
        type="text"
        className={styles.searchInput}
        size="small"
        inputRef={inputRef}
        InputProps={{
          classes: {
            root: styles.root,
            notchedOutline: styles.notchedOutline,
          },
          startAdornment: (
            <Icon
              className={styles.startAdornment}
              name="search"
              onClick={handleToggleInput}
              disabled={disabled}
              size="small"
            />
          ),
          endAdornment: EndAdornmentEl,
        }}
      />
    );
  }
);

export default SearchInput;
